import * as ACTIONS from '@/store/constants/actions.js';
import * as GETTERS from '@/store/constants/getters.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import { getQuickSearchResults } from '@/generated/console.js';
import { fetchCurrentUser, fetchUserByUuid, fetchUserByUuidWithoutSite } from '@/resources/user.js';
import { parseV2, parseV3 } from '@/store/users.js';
import { IS_OPERATOR } from '@/constants/filters.js';
import { ZERO_UUID } from 'commons/constants/parse.js';
import { isSociallyIngestedContent } from '@/util/social.js';
import { getDefaultUser } from './state.js';
import { VIAFOURA_ROOT_UUID } from 'commons/constants/parse.js';
import { fetchCurrentUserSections } from '../../../resources/iam';

const RECENT_CONTENT_PAGE_SIZE = 3;
const RECENT_CONTENT_SORT_ORDER = 'desc';
const RECENT_CONTENT_SORT_FIELD = 'date_created';

export const actions = {
    async [ACTIONS.FETCH_CURRENT_USER]({ getters, commit }) {
        const active_site_uuid = getters[GETTERS.ACTIVE_SITE_UUID];

        try {
            const currentUser = await fetchCurrentUser(active_site_uuid);
            const currentUserSections = await fetchCurrentUserSections();
            if (currentUserSections.roles.order[0] === VIAFOURA_ROOT_UUID && currentUserSections.roles.sections[VIAFOURA_ROOT_UUID].administrator) {
                currentUser.user_privilege = 'global_administrator';
            }
            commit(MUTATIONS.SET_CURRENT_USER, currentUser);
            return currentUser;
        }
        catch (e) {
            return Promise.reject(e);
        }
    },

    [ACTIONS.REGISTER_USER]({ commit }, { user, isV2 = true }) {
        const parsed = isV2 ? parseV2(user) : parseV3(user);
        commit(MUTATIONS.SET_USER, {
            uuid: parsed.uuid,
            user: parsed,
        });
        return Promise.resolve(parsed);
    },

    [ACTIONS.FETCH_USERS_RECENT_CONTENT]({ commit, dispatch }, { user_uuid }) {
        const params = {
            searchDefinition: {
                filters: [ {
                    field: 'actor_uuid',
                    operator: IS_OPERATOR,
                    values: [ user_uuid ],
                } ],
            },
            page_size: RECENT_CONTENT_PAGE_SIZE,
            sort_field: RECENT_CONTENT_SORT_FIELD,
            sort_order: RECENT_CONTENT_SORT_ORDER,
        };
        return getQuickSearchResults(params).then((response) => {
            Promise.all(response.content.map((item) => {
                delete item.tags; // Don't replace the tags!
                return dispatch(ACTIONS.SET_FETCHED_CONTENT, item);
            })).then(() => {
                commit(MUTATIONS.SET_USER, {
                    uuid: user_uuid,
                    user: {
                        comments: response.content.map(({ content_id }) => content_id),
                        total_comments: response.total_hits,
                    },
                });
            });
        });
    },

    [ACTIONS.FETCH_USER_BY_UUID]({ getters, dispatch, commit }, {
        uuid, site_uuid, origin, author_host_section_uuid,
    }) {
        // If its a socially ingested user, no need to fetch it
        if (isSociallyIngestedContent({ origin })) {
            return Promise.resolve(getters[GETTERS.USER_BY_ORIGIN]({
                actor_uuid: uuid,
                origin,
            }));
        }
        const user = getters[GETTERS.USER_BY_UUID](uuid);
        const default_user = getDefaultUser(getters[GETTERS.GET_TRANSLATION]('moderation.users.unassigned'));
        // If the user has been fetched for the site already, just return it
        if (user) {
            // if site uuid is set on the user, then its been fetched for the site and never needs to be fetched again
            if (user.site_uuid !== ZERO_UUID) {
                return Promise.resolve(user);
            }
        }
        // If the user has not been fetched at all
        // (The user could have been fetched, but not for the specific site. In this case, we still need to fetch again and set it)
        else {
            commit(MUTATIONS.SET_USER, {
                uuid,
                user: default_user,
            });
        }

        try {
            // First try to fetch the user for the site, if that fails, then try to fetch it for the author_host_section (syndication)
            return fetchUserByUuid(uuid, site_uuid)
                .then((user) =>
                    dispatch(ACTIONS.REGISTER_USER, {
                        user: {
                            ...user,
                            site_uuid,
                        },
                    }))
                .catch(() => fetchUserByUuid(uuid, author_host_section_uuid)
                    .then((user) =>
                        dispatch(ACTIONS.REGISTER_USER, {
                            user: {
                                ...user,
                                site_uuid,
                            },
                        }))
                    .catch(() => Promise.resolve(default_user)));
        }
        // If anything goes wrong, then just return the default user instead of breaking the entire queue
        catch (e) {
            return Promise.resolve(default_user);
        }
    },

    [ACTIONS.FETCH_USER_BY_UUID_WITHOUT_SITE]({ getters, dispatch, commit }, { uuid }) {
        const user = getters[GETTERS.USER_BY_UUID](uuid);
        if (user) {
            return Promise.resolve(user);
        }

        const default_user = getDefaultUser(getters[GETTERS.GET_TRANSLATION]('moderation.users.unassigned'));
        commit(MUTATIONS.SET_USER, {
            uuid,
            user: default_user,
        });
        return fetchUserByUuidWithoutSite(uuid)
            .then((user) => dispatch(ACTIONS.REGISTER_USER, { user }))
            .catch(() => Promise.resolve(default_user));
    },
};
